import React, { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HLetterLogo, LogoBackgroundColor } from "./logos";
import { GrayButton, GrayButton2, GradientButton } from "./buttons";
import {
  BannerBox,
  SideBarBox,
  InternalCenterBox,
  SideBarBookingBox,
  SideBarBookingTextBox,
} from "./boxes";
import {
  BannerTitle,
  BannerSubTitle,
  SideBarTitle,
  SideBarBookingTitle,
  SideBarBookingSubtitle,
  ProfileTitle,
  ProfileP,
} from "./texts";
import { SideBarCheck } from "./checkmarks";
import { UserData, UserBooking } from "../types/responseTypes";

import { ReactComponent as DownHeadIcon } from "./icons/arrowheads/downhead.svg";
import { ReactComponent as RightHeadIcon } from "./icons/arrowheads/righthead.svg";

import endSession from "../views/Home/endSession";

const backgroundColorMap: Record<LogoBackgroundColor, string> = {
  blue: "#5A6ED5",
  black: "#30323A",
  white: "#FFFFFF",
};


interface SideBarBookingProps {
  booking: UserBooking;
  selected: boolean;
  onClick: () => void;
}

export const SideBarBooking: React.FC<SideBarBookingProps> = ({
  booking,
  selected,
  onClick,
}) => {
  return (
    <SideBarBookingBox onClick={onClick}>
      <SideBarCheck selected={selected} />
      <SideBarBookingTextBox
        subtitle={
          <SideBarBookingSubtitle>
            {`${booking.commune}, ${booking.region}`}
          </SideBarBookingSubtitle>
        }
        title={<SideBarBookingTitle>{booking.address}</SideBarBookingTitle>}
      />
    </SideBarBookingBox>
  );
};

interface ProfileProps {
  userData: UserData;
  bookingIndex: number;
}

export const ProfileInfo: React.FC<ProfileProps> = ({
  userData,
  bookingIndex,
}) => {
  const navigate = useNavigate();
  return (
    <div className="w-full flex justify-between items-center py-4">
              <div className="flex flex-col">
                <div className="flex flex-col space-y-1 self-end">
                  <ProfileTitle>Tu cuenta</ProfileTitle>
                  <ProfileP>
                    {userData.firstName} {userData.lastName}
                  </ProfileP>
                  <ProfileP>{userData.rut}</ProfileP>
                  <ProfileP>{userData.phone}</ProfileP>
                  <ProfileP>{userData.email}</ProfileP>
                </div>
              </div>
              <div className="self-end flex flex-col pr-4 space-y-4">        
                <GrayButton
                  label="Cerrar Sesión"
                  onClick={() => endSession()}
                  w="138px"
                  h="34px"
                />
                <GrayButton
                  label="Editar Perfil"
                  onClick={() => navigate("/edituser", { state: { userData, bookingIndex } })}
                  w="138px"
                  h="34px"
                />
              </div>
            </div>
  );
};

interface HomeBannerProps {
  selectedBookingIndex: number;
  backgroundColor?: LogoBackgroundColor;
  logoH?: string;
  backLink?: string;
  children?: ReactNode;
  userBookings: UserBooking[];
  userData: UserData;
  onSelectBooking: (index: number) => void;
}

export const HomeBanner: React.FC<HomeBannerProps> = ({
  backgroundColor,
  logoH,
  backLink,
  children,
  userBookings,
  userData,
  selectedBookingIndex,
  onSelectBooking,
}) => {
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const handleToggleSidebar = () => setSidebarOpen(!isSidebarOpen);
  // const handleSelectBooking = (index: number) => {
  //   setSidebarOpen(false);
  // };

  const bannerH = calculateBannerHeight(logoH || "24px");

  return (
    <>
      <BannerBox
        backgroundColor={backgroundColorMap[backgroundColor || "white"]}
        height={bannerH}
      >
        <div className="flex items-center space-x-4">
          <HLetterLogo
            userData = {userData}
            backgroundColor={backgroundColor || "white"}
            h={logoH || "24px"}
          />
          {backLink && (
            <GrayButton2
              label="Volver"
              onClick={() => navigate(backLink, { state: { userData } })}
              w="100px" 
              h="40px"
              icon="leftarrow"
              //w="84px"
              //h="32px"
            />
          )}
        </div>

        <div
          className="flex items-start w-auto h-[39px] cursor-pointer px-6"
          onClick={handleToggleSidebar}
        >
          <div className="flex flex-col text-left">
            <BannerSubTitle>
              {`${userBookings[selectedBookingIndex]?.commune}, ${userBookings[selectedBookingIndex]?.region}`}
            </BannerSubTitle>
            <BannerTitle>
              {userBookings[selectedBookingIndex]?.address ||
                "No address available"}
            </BannerTitle>
          </div>
          {!isSidebarOpen && <DownHeadIcon className="w-4 h-4" />}
        </div>
        {children}
      </BannerBox>

      {isSidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={handleToggleSidebar}
          />
          <SideBarBox onClose={handleToggleSidebar}>
            <InternalCenterBox
              w="100%"
              h="72px"
              onClick={handleToggleSidebar}
              cursor="pointer"
            >
              <div className="flex items-center justify-between w-full px-4">
                <RightHeadIcon className="w-4 h-4 mr-2" />
                <div className="flex-1 flex justify-center">
                  <SideBarTitle>Direcciones</SideBarTitle>
                </div>
              </div>
            </InternalCenterBox>
            <div className="flex-grow overflow-y-auto">
              {userBookings.map((booking, index) => (
                <SideBarBooking
                  key={booking.bookingId}
                  booking={booking}
                  selected={selectedBookingIndex === index}
                  onClick={() => onSelectBooking(index)}
                />
              ))}
              <InternalCenterBox w="100%" h="116px">
                <GradientButton
                  label="Nueva Cotización"
                  onClick={() => window.open("https://ruuf.cl", "_blank")}
                />
              </InternalCenterBox>
            </div>
            <ProfileInfo userData={userData} bookingIndex={selectedBookingIndex}/>
          </SideBarBox>
        </>
      )}
    </>
  );
};

interface EditUserBannerProps {
  userData: UserData;
  backgroundColor?: LogoBackgroundColor;
  logoH?: string;
  backLink?: string;
  children?: ReactNode;
  bookingIndex?: number;
}

export const EditUserBanner: React.FC<EditUserBannerProps> = ({
  userData,
  backgroundColor,
  logoH,
  backLink,
  children,
  bookingIndex,
}) => {
  const navigate = useNavigate();
  const bannerH = calculateBannerHeight(logoH || "24px");

  return (
    <>
      <BannerBox
        backgroundColor={backgroundColorMap[backgroundColor || "white"]}
        height={bannerH}
      >
        <div className="flex items-center space-x-4">
          <HLetterLogo
            userData = {userData}
            backgroundColor={backgroundColor || "white"}
            h={logoH || "24px"}
          />
          {backLink && (
            <GrayButton
              label="Volver"
              onClick={() => navigate(backLink, { state: { userData, bookingIndex} })}
              w="120px"
              h="44px"
              icon="lefthead"
            />
          )}
        </div>
        {children}
      </BannerBox>
    </>
  );
};

// Helper function to calculate banner height
const calculateBannerHeight = (logoHeight: string): string => {
  const match = logoHeight.match(/^(\d+(?:\.\d+)?)(px|%)$/);
  if (!match) return logoHeight;

  const [, numStr, unit] = match;
  const num = parseFloat(numStr);
  return `${num * 3}${unit}`;
};
